import {
  forwardRef,
  type ReactNode,
  type TextareaHTMLAttributes,
} from "react";

import "./TextArea.scss";

type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & {
  label: string;
  error?: string;
  hint?: string;
  icon?: ReactNode;
};

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      id,
      label,
      error,
      hint,
      icon,
      className = "",
      required,
      disabled,
      rows = 5,
      ...textareaProps
    },
    ref
  ) => {
    const textareaId = id ?? textareaProps.name;

    const errorId = textareaId
      ? `${textareaId}-error`
      : undefined;

    const hintId = textareaId
      ? `${textareaId}-hint`
      : undefined;

    const describedBy = [
      error ? errorId : undefined,
      !error && hint ? hintId : undefined,
    ]
      .filter(Boolean)
      .join(" ");

    return (
      <div
        className={[
          "text-area",
          error ? "text-area--error" : "",
          disabled ? "text-area--disabled" : "",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <label
          className="text-area__label"
          htmlFor={textareaId}
        >
          {label}

          {required && (
            <span
              className="text-area__required"
              aria-hidden="true"
            >
              *
            </span>
          )}
        </label>

        <div className="text-area__control">
          {icon && (
            <span
              className="text-area__icon"
              aria-hidden="true"
            >
              {icon}
            </span>
          )}

          <textarea
            {...textareaProps}
            ref={ref}
            id={textareaId}
            rows={rows}
            required={required}
            disabled={disabled}
            aria-invalid={Boolean(error)}
            aria-describedby={
              describedBy || undefined
            }
            className={[
              "text-area__field",
              icon
                ? "text-area__field--with-icon"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
          />
        </div>

        {error ? (
          <p
            id={errorId}
            className="text-area__message text-area__message--error"
            role="alert"
          >
            {error}
          </p>
        ) : hint ? (
          <p
            id={hintId}
            className="text-area__message"
          >
            {hint}
          </p>
        ) : null}
      </div>
    );
  }
);

TextArea.displayName = "TextArea";